import { hasMeaningfulTyreSerial } from './tyre-parse.js';
import { parseSeasonPoolPayload } from './tyre-verify.js';

/** @typedef {{ slot: number, serial: string, filled: boolean }} SeasonReportSlot */
/** @typedef {{ startNumber: number, serials: string[] }} SeasonReportCar */

export const MAX_SEASON_REPORT_SLOTS = 24;

/**
 * @param {unknown} value
 * @returns {string}
 */
export function escapeHtml(value) {
  return String(value ?? '')
    .replace(/&/gu, '&amp;')
    .replace(/</gu, '&lt;')
    .replace(/>/gu, '&gt;')
    .replace(/"/gu, '&quot;');
}

/**
 * @param {unknown} raw
 * @returns {number}
 */
export function normalizeStartNumber(raw) {
  const n = Number.parseInt(String(raw ?? '').trim(), 10);

  if (!Number.isFinite(n) || n < 1 || n > 999) {
    return 0;
  }

  return n;
}

/**
 * Build slots 1–24 for a single-car detail view.
 *
 * @param {string[]} poolSerials
 * @param {number} [maxSlots]
 * @returns {{ count: number, remaining: number, maxSlots: number, slots: SeasonReportSlot[] }}
 */
export function buildSeasonReportSlots(poolSerials, maxSlots = MAX_SEASON_REPORT_SLOTS) {
  const cap     = Math.max(0, maxSlots);
  const serials = Array.isArray(poolSerials) ? poolSerials.slice(0, cap) : [];
  /** @type {SeasonReportSlot[]} */
  const slots   = [];

  for (let i = 0; i < cap; i += 1) {
    const serial = serials[i] || '';

    slots.push({
      slot:   i + 1,
      serial,
      filled: Boolean(serial)
    });
  }

  return {
    count:     serials.length,
    remaining: cap - serials.length,
    maxSlots:  cap,
    slots
  };
}

/**
 * @param {unknown} entry
 * @returns {SeasonReportCar | null}
 */
export function parseSeasonReportCarEntry(entry) {
  if (!entry || typeof entry !== 'object') {
    return null;
  }

  const startNumber = normalizeStartNumber(
    entry.startNumber ?? entry.start ?? entry.sn ?? entry.car ?? entry.number
  );

  if (!startNumber) {
    return null;
  }

  /** @type {string[]} */
  let serials = [];

  if (Array.isArray(entry.tyres)) {
    serials = parseSeasonPoolPayload(entry.tyres);
  }
  else if (Array.isArray(entry.tyreSerials)) {
    serials = entry.tyreSerials
      .map(String)
      .filter(serial => hasMeaningfulTyreSerial(serial));
  }
  else if (Array.isArray(entry.serials)) {
    serials = parseSeasonPoolPayload(entry.serials);
  }
  else if (Array.isArray(entry.pool)) {
    serials = parseSeasonPoolPayload(entry.pool);
  }

  return { startNumber, serials };
}

/**
 * Parse aggregate fleet payloads from the trackside API.
 *
 * @param {unknown} data
 * @returns {SeasonReportCar[] | null}
 */
export function parseSeasonReportFleetPayload(data) {
  if (data == null) {
    return null;
  }

  /** @type {SeasonReportCar[]} */
  const cars = [];

  if (Array.isArray(data)) {
    for (const entry of data) {
      const car = parseSeasonReportCarEntry(entry);

      if (car) {
        cars.push(car);
      }
    }
  }
  else if (typeof data === 'object') {
    const list = data.cars ?? data.teams ?? data.items ?? data.entries ?? data.data;

    if (Array.isArray(list)) {
      return parseSeasonReportFleetPayload(list);
    }

    for (const [key, value] of Object.entries(data)) {
      const startNumber = normalizeStartNumber(key);

      if (!startNumber) {
        continue;
      }

      const serials = Array.isArray(value) ? parseSeasonPoolPayload(value) : [];
      cars.push({ startNumber, serials });
    }
  }

  return mergeSeasonReportCars(cars);
}

/**
 * @param {unknown} data
 * @returns {number[]}
 */
export function extractStartNumbersFromSeriesIndex(data) {
  /** @type {number[]} */
  const numbers = [];

  if (Array.isArray(data)) {
    for (const entry of data) {
      const sn = normalizeStartNumber(
        entry?.startNumber ?? entry?.start ?? entry?.sn ?? entry?.car ?? entry?.number
      );

      if (sn) {
        numbers.push(sn);
      }
    }
  }
  else if (data && typeof data === 'object') {
    const list = data.cars ?? data.teams ?? data.items ?? data.entries ?? data.data;

    if (Array.isArray(list)) {
      return extractStartNumbersFromSeriesIndex(list);
    }

    for (const key of Object.keys(data)) {
      const sn = normalizeStartNumber(key);

      if (sn) {
        numbers.push(sn);
      }
    }
  }

  return [...new Set(numbers)].sort((a, b) => a - b);
}

/**
 * Merge duplicate start numbers and sort ascending.
 *
 * @param {SeasonReportCar[]} cars
 * @returns {SeasonReportCar[]}
 */
export function mergeSeasonReportCars(cars) {
  /** @type {Map<number, string[]>} */
  const byStart = new Map();

  for (const car of cars) {
    const prev = byStart.get(car.startNumber) ?? [];
    const next = prev.concat(car.serials);
    byStart.set(car.startNumber, [...new Set(next)]);
  }

  return [...byStart.entries()]
    .map(([startNumber, serials]) => ({ startNumber, serials }))
    .sort((a, b) => a.startNumber - b.startNumber);
}

/**
 * @param {SeasonReportCar[]} cars
 * @param {string} [query]
 * @returns {SeasonReportCar[]}
 */
export function filterSeasonReportCars(cars, query) {
  const q = String(query ?? '').trim();

  if (!q) {
    return cars;
  }

  return cars.filter(car => String(car.startNumber).includes(q));
}

/**
 * @param {SeasonReportCar[]} cars
 * @returns {{ carCount: number, tyreTotal: number, listedCars: number }}
 */
export function summarizeSeasonReportFleet(cars) {
  const withTyres = cars.filter(car => car.serials.length > 0);
  const tyreTotal = withTyres.reduce((sum, car) => sum + car.serials.length, 0);

  return {
    carCount:    withTyres.length,
    tyreTotal,
    listedCars:  cars.length
  };
}

/**
 * @param {{ carCount: number, tyreTotal: number }} summary
 * @param {string} [seriesName]
 * @returns {string}
 */
export function formatSeasonReportFleetSubtitle(summary, seriesName = 'V8 Thunder Cars') {
  if (summary.carCount === 0) {
    return `No registered tyres this season (${ seriesName }).`;
  }

  return `${ summary.carCount } cars · ${ summary.tyreTotal } tyres registered (${ seriesName }).`;
}

/**
 * @param {SeasonReportCar} car
 * @returns {string}
 */
export function renderSeasonReportCarHtml(car) {
  const count = car.serials.length;
  const rows  = car.serials.map((serial, index) => (
    `<li class="season-report__row is-filled">`
    + `<span class="season-report__slot">${ index + 1 }</span>`
    + `<span class="season-report__serial">${ escapeHtml(serial) }</span>`
    + '</li>'
  )).join('');

  return (
    `<article class="season-report-car" data-start="${ car.startNumber }">`
    + `<header class="season-report-car__header">`
    + `<h2 class="season-report-car__title">#${ car.startNumber }</h2>`
    + `<span class="season-report-car__count">${ count } / ${ MAX_SEASON_REPORT_SLOTS }</span>`
    + '</header>'
    + (count
      ? `<ol class="season-report-car__tyres">${ rows }</ol>`
      : '<p class="season-report-car__empty">No tyres registered.</p>')
    + '</article>'
  );
}

/**
 * @param {SeasonReportCar[]} cars
 * @param {{ includeEmpty?: boolean }} [options]
 * @returns {string}
 */
export function renderSeasonReportFleetHtml(cars, { includeEmpty = false } = {}) {
  const visible = includeEmpty ? cars : cars.filter(car => car.serials.length > 0);

  if (!visible.length) {
    return '<p class="season-report__empty">No cars with registered tyres.</p>';
  }

  return visible.map(renderSeasonReportCarHtml).join('');
}

/**
 * @param {{ count: number, remaining: number, maxSlots: number, slots: SeasonReportSlot[] }} report
 * @returns {string}
 */
export function renderSeasonReportListHtml(report) {
  if (!report.slots.length) {
    return '<li class="season-report__empty">No slots configured.</li>';
  }

  return report.slots.map(({ slot, serial, filled }) => {
    const klass = filled ? 'season-report__row is-filled' : 'season-report__row is-empty';
    const label = filled
      ? `Tyre ${ slot }, ${ serial }`
      : `Tyre ${ slot }, empty`;

    return (
      `<li class="${ klass }" data-filled="${ filled ? 'true' : 'false' }" aria-label="${ escapeHtml(label) }">`
      + `<span class="season-report__slot">${ slot }</span>`
      + `<span class="season-report__serial">${ filled ? escapeHtml(serial) : '—' }</span>`
      + '</li>'
    );
  }).join('');
}
